import { computeUtilization, parseRawAmount } from '../../../../utils/parsing'
import { exchangeRateAccumulator } from '../../accumulator'
import { LENDER_MODE_NO_MODE } from '../../../../assets'
import { createMarketUid } from '../../../../utils'
import { lenderShortName } from '../../../../utils/marketName'
import { BenqiMarketMetadata, getBenqiRates } from '../getters/benqi'
import {
  RESERVE_MANTISSA_DECIMALS,
  resolveMarketPrice,
  ConvertEntryOpts,
} from './types'
import { DEFAULT_CLOSE_FACTOR, DEFAULT_LIQUIDATION_PENALTY } from './risk'
import { cashLiquidity } from './liquidity'

// Both BENQI variants return all markets via getMarketMetadataForAllMarkets().
// The lens already provides totalUnderlyingSupply (underlying-denominated),
// so we don't need to compute it from exchangeRate * cTokenSupply.
export function convertBenqiEntry(
  opts: ConvertEntryOpts & { chainId: string; parsed: BenqiMarketMetadata },
) {
  const { parsed, asset, chainId, lender, prices, additionalYields, cToken } =
    opts

  const rates = getBenqiRates(parsed, chainId)
  const price = resolveMarketPrice(prices, asset, chainId)
  const decs = asset.decimals as number

  const collateralFactor = Number(
    parseRawAmount(
      parsed.collateralFactor.toString(),
      RESERVE_MANTISSA_DECIMALS,
    ),
  )

  // totalUnderlyingSupply is already in underlying token units (raw)
  const totalDeposits = Number(
    parseRawAmount(parsed.totalUnderlyingSupply.toString(), decs),
  )
  const totalDebt = Number(parseRawAmount(parsed.totalBorrows.toString(), decs))
  const liquidity =
    cashLiquidity(parsed.cash, decs) ?? totalDeposits - totalDebt

  const poolId = asset.address
  const marketUid = createMarketUid(chainId, lender, cToken)

  const collateralActive = !parsed.mintPaused
  const borrowingEnabled = !parsed.borrowPaused

  return {
    marketUid,
    name: lenderShortName(lender) + ' ' + (asset?.symbol ?? ''),
    poolId,
    asset,
    accumulator: exchangeRateAccumulator(parsed.exchangeRate),
    depositRate: rates.depositRate,
    variableBorrowRate: rates.variableBorrowRate,
    stableBorrowRate: 0,
    totalLiquidity: liquidity,
    borrowLiquidity: liquidity,
    totalLiquidityUSD: liquidity * price,
    borrowLiquidityUSD: liquidity * price,
    totalDebt,
    totalDebtUSD: totalDebt * price,
    totalDeposits,
    totalDepositsUSD: totalDeposits * price,
    totalDebtStable: 0,
    totalDebtStableUSD: 0,
    utilization: computeUtilization(totalDeposits, totalDebt),
    intrinsicYield: additionalYields?.intrinsicYields?.[asset.assetGroup] ?? 0,
    config: {
      [LENDER_MODE_NO_MODE]: {
        category: LENDER_MODE_NO_MODE,
        borrowCollateralFactor: collateralFactor,
        collateralFactor,
        borrowFactor: 1,
        liquidationPenalty: DEFAULT_LIQUIDATION_PENALTY,
        closeFactor: DEFAULT_CLOSE_FACTOR,
        collateralDisabled: !collateralActive,
        debtDisabled: !borrowingEnabled,
      },
    },
    closeFactor: DEFAULT_CLOSE_FACTOR,
    hasStable: false,
    underlying: asset.address,
    isActive: !parsed.mintPaused || !parsed.borrowPaused,
    isFrozen: parsed.mintPaused && parsed.borrowPaused,
    rewards: [],
    collateralActive,
    borrowingEnabled,
    depositsEnabled: !parsed.mintPaused,
    params: {
      metadata: {
        cToken,
        exchangeRate: parsed.exchangeRate.toString(),
        cTokenDecimals: parsed.qiTokenDecimals,
      },
    },
  }
}
